import './poultryDesign.css';
import Segmented from '../shared/Segmented.js';
import Button from '../shared/Button.js';
// Auto-extracted by Phase 2 Round 2 (verbatim). See MIGRATION_PLAN §6.
import React from 'react';
import type {CSSProperties} from 'react';
import {getProgramColor} from '../lib/programColors.js';
import {getReadableText} from '../lib/styles.js';
import {useAppLocation as useLocation, useAppNavigate as useNavigate} from '../app/navigation/index.js';
import {recordSeqNavOptions, dailySeqItems} from '../lib/recordSequence.js';
import type {DailyReportRecord} from '../lib/dailyReportsApi.js';
import {csvFilename, downloadCsv, rowsToCsv} from '../lib/csvExport.js';
import {buildBroilerDailyExportColumns} from '../lib/dailyReportExports.js';
import {printRows} from '../lib/printExport.js';
import {createSavedView, updateSavedView, deleteSavedView} from '../lib/savedViewsApi.js';
import {savedViewsErrorMessage, useSavedViewsData} from '../shared/savedViewsData.js';
// Loaded on the first Add Report click (LazyAdminAddReportModal), not at startup.
import AdminAddReportModal from '../shared/LazyAdminAddReportModal.tsx';
import OperationalListEmptyState from '../shared/OperationalListEmptyState.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import InlineNotice from '../shared/InlineNotice.jsx';
// eslint-disable-next-line no-unused-vars -- JSX-only use (eslint flat config has no react/jsx-uses-vars rule)
import {DailyCardList, feedLbsVal, tagVal, gritVal, check, mortText, commentText} from '../shared/DailyRecordCards.jsx';
import type {Notice} from '../shared/InlineNotice.jsx';
import type {DailyPhotoRef} from '../shared/DailyPhotoThumbnails.jsx';
import {usePersistentViewState} from '../lib/usePersistentViewState.js';
const PoultryDailyPage = React.lazy(() => import('./PoultryDailyPage.tsx'));
import {RESOURCE} from '../app/data/routeDataCatalog.js';
import type {BroilerDailysBatchDirectoryData} from '../app/data/routeDataCatalog.js';
import {useRouteResource} from '../app/data/useRouteResource.js';
import {usePaginatedResource} from '../app/data/usePaginatedResource.js';
import {sb} from '../lib/supabase.js';
import {getBatchColor} from '../lib/broiler.js';
import {fmtDate as fmt} from '../lib/dateUtils.js';
import {matchesTeamMemberFilter, teamMemberOptions, teamMemberSelectValue} from '../lib/teamMemberFilter.js';
import {useAuth} from '../contexts/AuthContext.jsx';
import type {AuthState} from '../contexts/AuthContext.jsx';
import Header from '../shared/Header.jsx';

const BROILER_DAILYS_SURFACE_KEY = 'broiler.dailys';
// Frozen module constant, not an inline object literal: useRouteResource
// serializes params into its key, and a fresh {} every render would rebuild the
// key and re-run everything downstream of it forever.
const BROILER_DAILYS_DIRECTORY_PARAMS = Object.freeze({});
const VALID_BROILER_DAILY_SOURCE_FILTERS = new Set(['all', 'daily', 'addfeed']);

// `batches` is NOT a prop from BroilerEntry any more.
//
// It was App's former-eager ppp-v4 boot result, threaded down through a single
// `batches={batches}` hand-off. BroilerDailysRouter reads it itself now,
// through broiler.dailys.batchDirectory, so `broilerdailys` retires from the
// frozen app_store consumer list in
// tests/fixtures/former-eager-dependencies.json. It is still passed from the
// ROUTER to the record page below (the hub stopped reading it when its edit
// modal went in E12a), which is a different thing entirely: the
// provenance derivation scores a hand-off in the DOMAIN ENTRY, because that is
// where a boot value enters the route.
type BroilerDailysProps = {
  refreshDailys?: (kind: string) => void | Promise<void>;
};
function errorMessage(value: unknown): string {
  const candidate = value && typeof value === 'object' ? (value as {message?: unknown}).message : undefined;
  return String(candidate || value);
}
type BroilerDailyRecord = DailyReportRecord & {
  id: string;
  date: string;
  batch_label?: string | null;
  team_member?: string | null;
  feed_type?: string | null;
  feed_lbs?: number | string | null;
  grit_lbs?: number | string | null;
  mortality_count?: number | string | null;
  mortality_reason?: string | null;
  group_moved?: boolean | null;
  waterer_checked?: boolean | null;
  comments?: string | null;
  source?: string | null;
  photos?: DailyPhotoRef[] | null;
};
type SavedViewRow = {
  id: string;
  name: string;
  visibility?: string | null;
  owner_profile_id?: string | null;
  view_state?: unknown;
};
type BroilerDailysViewState = {
  fBatch: string;
  fTeam: string;
  fFrom: string;
  fTo: string;
  srcFilter: string;
};

function BroilerDailysRouter(props: BroilerDailysProps) {
  const {authState} = useAuth();
  const location = useLocation();
  // The batch directory is read HERE, above the index/record branch, because
  // that is the router the record page hangs off — and because it is exactly
  // where the BroilerEntry prop hand-off used to land, which is what makes
  // broiler.dailys.batchDirectory a replacement rather than an addition.
  const directoryResource = useRouteResource(RESOURCE.BROILER_DAILYS_BATCH_DIRECTORY, BROILER_DAILYS_DIRECTORY_PARAMS);
  const directory = directoryResource.data as BroilerDailysBatchDirectoryData | null;
  // Still `unknown[]` at this boundary, exactly as the prop was:
  // the record page narrows to BroilerBatchRow where it reads a column.
  const batches = directory?.batches || [];
  const dailyId = location.pathname.startsWith('/broiler/dailys/')
    ? location.pathname.slice('/broiler/dailys/'.length) || null
    : null;
  if (dailyId) {
    return React.createElement(
      React.Suspense,
      {
        fallback: React.createElement(
          'div',
          {role: 'status', 'aria-live': 'polite', style: {padding: '2rem', textAlign: 'center'}},
          'Loading Poultry Daily Record…',
        ),
      },
      React.createElement(PoultryDailyPage, {
        sb,
        fmt,
        authState,
        batches,
      }),
    );
  }
  return React.createElement(BroilerDailysHub, props);
}

const BroilerDailysHub: React.FC<BroilerDailysProps> = ({refreshDailys}) => {
  const {authState} = useAuth() as {authState: AuthState};
  const {useState, useEffect} = React;
  // Rows the admin Add Report modal just created, projected over the server
  // pages so they show before the next read. (The edit override and delete hide
  // projections went with the unreachable edit modal, E12a.)
  const [createdRecords, setCreatedRecords] = useState<BroilerDailyRecord[]>([]);
  const [fBatch, setFBatch] = usePersistentViewState('broiler.dailys.batchFilter', '');
  const [fTeam, setFTeam] = usePersistentViewState('broiler.dailys.teamFilter', '');
  const [fFrom, setFFrom] = usePersistentViewState('broiler.dailys.fromFilter', '');
  const [fTo, setFTo] = usePersistentViewState('broiler.dailys.toFilter', '');
  const [srcFilter, setSrcFilter] = usePersistentViewState('broiler.dailys.sourceFilter', 'all');
  const [reloadKey, setReloadKey] = useState(0);
  const [exportNotice, setExportNotice] = useState('');
  const [selectedViewId, setSelectedViewId] = useState('');
  const [showSaveViewForm, setShowSaveViewForm] = useState(false);
  const [saveViewName, setSaveViewName] = useState('');
  const [saveViewVisibility, setSaveViewVisibility] = useState('private');
  const [savedViewBusy, setSavedViewBusy] = useState(false);
  const [savedViewNotice, setSavedViewNotice] = useState<Notice | null>(null);
  const myProfileId = authState ? authState.user?.id || null : null;
  const savedViewsQuery = useSavedViewsData(BROILER_DAILYS_SURFACE_KEY, {client: sb});
  const savedViews = savedViewsQuery.data as SavedViewRow[];
  const savedViewsError = savedViewsQuery.error ? savedViewsErrorMessage(savedViewsQuery.error) : null;
  const savedViewsLoading = savedViewsQuery.loading;
  const loadSavedViews = savedViewsQuery.refetch;
  // The list's rows, its cursor, its cancellation and its stale-key protection
  // are the paginated resource's now. `reload` is in the page key on purpose:
  // Retry must start a NEW complete sequence rather than same-key refetch an
  // already-partial list — the same contract cattle.dailys.list documents.
  const dailysParams = React.useMemo(() => ({reload: reloadKey}), [reloadKey]);
  const dailysResource = usePaginatedResource<BroilerDailyRecord, number>(RESOURCE.BROILER_DAILYS_LIST, dailysParams);
  const {
    error: listError,
    hasMore: listHasMore,
    loadMore,
    loadMoreError: listLoadMoreError,
    loadingMore: listLoadingMore,
  } = dailysResource;
  const loading = dailysResource.loading;
  const loadError = React.useMemo<Notice | null>(
    () =>
      listError
        ? {
            kind: 'error',
            message:
              'Could not load daily reports. Please refresh the page. (' +
              (errorMessage(listError) || String(listError)) +
              ')',
          }
        : listLoadMoreError
          ? {
              kind: 'error',
              message:
                'Could not load more daily reports. (' +
                (errorMessage(listLoadMoreError) || String(listLoadMoreError)) +
                ')',
            }
          : null,
    [listError, listLoadMoreError],
  );

  // The server pages, with this session's local mutations projected over them.
  // Order is what the old single array produced: newly created rows first, then
  // the pages in server order, each row replaced by its override and skipped if
  // hidden. De-duplicated by id because the append loop it replaces did that.
  const records = React.useMemo(() => {
    const seen = new Set<string>();
    const rows: BroilerDailyRecord[] = [];
    for (const row of [...createdRecords, ...dailysResource.rows]) {
      if (seen.has(String(row.id))) continue;
      seen.add(String(row.id));
      rows.push(row);
    }
    return rows;
  }, [createdRecords, dailysResource.rows]);

  // A Retry is a new server sequence, so its rows are authoritative again and
  // no transient local projection may leak into the new key.
  useEffect(() => {
    setCreatedRecords([]);
  }, [reloadKey]);

  // The eager "load every page" behaviour is unchanged; only the cursor moved.
  // `listLoadMoreError` is in the guard so a failed append stops the loop
  // instead of retrying it forever — the old `pgLoading` ref simply never reset
  // on failure, which had the same effect by accident.
  useEffect(() => {
    if (listHasMore && !listLoadingMore && !listLoadMoreError) {
      void loadMore();
    }
  }, [listHasMore, listLoadingMore, listLoadMoreError, loadMore]);

  useEffect(() => {
    if (savedViewsLoading || savedViewsError) return;
    setSelectedViewId((cur) => (cur && savedViews.some((row) => row.id === cur) ? cur : ''));
  }, [savedViews, savedViewsError, savedViewsLoading]);

  const navigate = useNavigate();

  const [showAddModal, setShowAddModal] = useState(false);

  const batchOpts = [...new Set(records.map((r) => r.batch_label).filter((value): value is string => !!value))].sort();
  const teamOpts = teamMemberOptions(records.map((r) => r.team_member));
  let filtered = records.filter(
    (r) =>
      (!fBatch || r.batch_label === fBatch) &&
      matchesTeamMemberFilter(r.team_member, fTeam) &&
      (!fFrom || r.date >= fFrom) &&
      (!fTo || r.date <= fTo) &&
      (srcFilter === 'all' ||
        (srcFilter === 'daily' && r.source !== 'add_feed_webform') ||
        (srcFilter === 'addfeed' && r.source === 'add_feed_webform')),
  );
  const selectedView = savedViews.find((v) => v.id === selectedViewId) || null;
  const selectedViewIsMine = !!(selectedView && myProfileId && selectedView.owner_profile_id === myProfileId);

  function broilerDailysViewState() {
    return {
      fBatch: fBatch || '',
      fTeam: fTeam || '',
      fFrom: fFrom || '',
      fTo: fTo || '',
      srcFilter: VALID_BROILER_DAILY_SOURCE_FILTERS.has(srcFilter) ? srcFilter : 'all',
    };
  }

  function applyBroilerDailysSavedView(view: SavedViewRow | null | undefined) {
    if (!view) return;
    const st = (view.view_state || {}) as BroilerDailysViewState;
    setFBatch(typeof st.fBatch === 'string' ? st.fBatch : '');
    setFTeam(typeof st.fTeam === 'string' ? st.fTeam : '');
    setFFrom(typeof st.fFrom === 'string' ? st.fFrom : '');
    setFTo(typeof st.fTo === 'string' ? st.fTo : '');
    setSrcFilter(VALID_BROILER_DAILY_SOURCE_FILTERS.has(st.srcFilter) ? st.srcFilter : 'all');
    setSavedViewNotice(null);
  }

  function onSelectSavedView(id: string) {
    setSelectedViewId(id);
    setSavedViewNotice(null);
    if (!id) return;
    applyBroilerDailysSavedView(savedViews.find((v) => v.id === id));
  }

  function openSaveViewForm() {
    setSaveViewName('');
    setSaveViewVisibility('private');
    setSavedViewNotice(null);
    setShowSaveViewForm(true);
  }

  async function submitSaveView() {
    const name = saveViewName.trim();
    if (!name) {
      setSavedViewNotice({kind: 'error', message: 'Name the view before saving.'});
      return;
    }
    setSavedViewBusy(true);
    try {
      const created = (await createSavedView(sb, {
        surfaceKey: BROILER_DAILYS_SURFACE_KEY,
        name,
        visibility: saveViewVisibility,
        viewState: broilerDailysViewState(),
      })) as SavedViewRow;
      setShowSaveViewForm(false);
      setSavedViewNotice({kind: 'success', message: 'Saved view "' + name + '".'});
      await loadSavedViews();
      if (created?.id) setSelectedViewId(created.id);
    } catch (e) {
      setSavedViewNotice({kind: 'error', message: 'Save view failed: ' + errorMessage(e)});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function updateSelectedView() {
    if (!selectedView || !selectedViewIsMine) return;
    setSavedViewBusy(true);
    try {
      await updateSavedView(sb, selectedView.id, {viewState: broilerDailysViewState()});
      await loadSavedViews();
      setSavedViewNotice({
        kind: 'success',
        message: 'Updated "' + selectedView.name + '" to the current filters.',
      });
    } catch (e) {
      setSavedViewNotice({kind: 'error', message: 'Update view failed: ' + errorMessage(e)});
    } finally {
      setSavedViewBusy(false);
    }
  }

  async function proceedDeleteSelectedView(view: SavedViewRow) {
    setSavedViewBusy(true);
    try {
      await deleteSavedView(sb, view.id);
      setSelectedViewId('');
      await loadSavedViews();
      setSavedViewNotice({kind: 'success', message: 'Deleted saved view "' + view.name + '".'});
    } catch (e) {
      setSavedViewNotice({kind: 'error', message: 'Delete view failed: ' + errorMessage(e)});
    } finally {
      setSavedViewBusy(false);
    }
  }

  function deleteSelectedView() {
    if (!selectedView || !selectedViewIsMine || !window._wcfConfirmDelete) return;
    window._wcfConfirmDelete('Delete saved view "' + selectedView.name + '"?', () => {
      void proceedDeleteSelectedView(selectedView);
    });
  }

  function handleExportCsv() {
    const columns = buildBroilerDailyExportColumns();
    const ok = downloadCsv(csvFilename('broiler-dailys'), rowsToCsv(columns, filtered));
    setExportNotice(ok ? '' : 'CSV export is only available in the browser.');
  }

  function handlePrintRows() {
    const columns = buildBroilerDailyExportColumns();
    const ok = printRows({
      title: 'Broiler Dailys',
      subtitle: filtered.length + ' filtered daily reports',
      columns,
      rows: filtered,
    });
    setExportNotice(ok ? '' : 'Print is only available in the browser.');
  }

  const totalFeed = filtered.reduce((s, r) => s + (parseFloat(String(r.feed_lbs)) || 0), 0);
  const totalMort = filtered.reduce((s, r) => s + (parseInt(String(r.mortality_count)) || 0), 0);
  const fi: CSSProperties = {
    padding: '6px 10px',
    borderRadius: 10,
    border: '1px solid var(--border-strong)',
    fontSize: 12,
    fontFamily: 'inherit',
    background: 'white',
    width: 'auto',
  };
  const myViews = savedViews.filter((v) => myProfileId && v.owner_profile_id === myProfileId);
  const publicOtherViews = savedViews.filter(
    (v) => v.visibility === 'public' && !(myProfileId && v.owner_profile_id === myProfileId),
  );
  const savedViewGhostBtnS: CSSProperties = {
    padding: '6px 12px',
    borderRadius: 10,
    border: '1px solid var(--border-strong)',
    background: 'white',
    color: 'var(--ink)',
    fontSize: 12,
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
    whiteSpace: 'nowrap',
  };
  const savedViewPrimaryBtnS: CSSProperties = {
    ...savedViewGhostBtnS,
    border: `1px solid ${getProgramColor('broiler')}`,
    color: getProgramColor('broiler'),
  };
  const savedViewRadioLabelS: CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 12,
    color: 'var(--ink)',
    cursor: 'pointer',
  };

  return (
    <div
      style={{minHeight: '100vh', background: 'var(--bg-page)'}}
      data-broiler-dailys-loaded={loading || loadError ? 'false' : 'true'}
    >
      <Header />
      <div style={{padding: '1rem', maxWidth: 1180, margin: '0 auto'}}>
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 12,
            flexWrap: 'wrap',
            gap: 8,
          }}
        >
          <div>
            <h1 className="poultry-page-title">Daily Reports</h1>
            <div style={{fontSize: 12, color: 'var(--ink-muted)', marginTop: 2}}>
              {records.length.toLocaleString()} total
            </div>
          </div>
          <div style={{display: 'flex', gap: 8}}>
            <Button variant="primary" onClick={() => setShowAddModal(true)}>
              + Add Report
            </Button>
          </div>
        </div>
        {showAddModal && (
          <AdminAddReportModal
            sb={sb}
            formType="broiler"
            onClose={() => setShowAddModal(false)}
            onSaved={(recs: BroilerDailyRecord[]) => {
              setCreatedRecords((p) => [...recs, ...p]);
              refreshDailys && refreshDailys('broiler');
            }}
          />
        )}
        {!loadError && (
          <>
            <div
              data-broiler-dailys-saved-views-row
              style={{
                background: 'white',
                border: '1px solid var(--border)',
                borderRadius: 10,
                padding: '10px 14px',
                marginBottom: 8,
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                flexWrap: 'wrap',
              }}
            >
              <span style={{fontSize: 11, color: 'var(--ink-muted)', fontWeight: 600}}>Saved views</span>
              {savedViewsError ? (
                <span style={{fontSize: 12, color: '#b91c1c'}} data-broiler-dailys-saved-views-error>
                  Saved views unavailable. Filters still work.
                </span>
              ) : (
                <>
                  <select
                    data-broiler-dailys-saved-view-select
                    value={selectedViewId}
                    disabled={savedViewsLoading}
                    onChange={(e) => onSelectSavedView(e.target.value)}
                    style={{...fi, minWidth: 200}}
                  >
                    <option value="">{savedViewsLoading ? 'Loading...' : 'Select a saved view'}</option>
                    {myViews.length > 0 && (
                      <optgroup label="My views">
                        {myViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name + (v.visibility === 'public' ? ' - public' : ' - private')}
                          </option>
                        ))}
                      </optgroup>
                    )}
                    {publicOtherViews.length > 0 && (
                      <optgroup label="Public views">
                        {publicOtherViews.map((v) => (
                          <option key={v.id} value={v.id}>
                            {v.name}
                          </option>
                        ))}
                      </optgroup>
                    )}
                  </select>
                  {selectedViewIsMine && (
                    <>
                      <Button
                        type="button"
                        data-broiler-dailys-saved-view-update
                        onClick={updateSelectedView}
                        disabled={savedViewBusy}
                      >
                        Update to current
                      </Button>
                      <Button
                        variant="danger"
                        type="button"
                        data-broiler-dailys-saved-view-delete
                        onClick={deleteSelectedView}
                        disabled={savedViewBusy}
                      >
                        Delete
                      </Button>
                    </>
                  )}
                  <span style={{flex: 1}} />
                  {savedViewNotice && (
                    <span style={{fontSize: 12, color: savedViewNotice.kind === 'success' ? '#065f46' : '#b91c1c'}}>
                      {savedViewNotice.message}
                    </span>
                  )}
                  <Button
                    type="button"
                    data-broiler-dailys-saved-view-save-open
                    onClick={openSaveViewForm}
                    disabled={savedViewBusy || savedViewsLoading}
                  >
                    Save current view
                  </Button>
                </>
              )}
            </div>
            {showSaveViewForm && (
              <div
                data-broiler-dailys-saved-view-form
                style={{
                  background: 'white',
                  border: '1px solid #fde68a',
                  borderRadius: 10,
                  padding: '10px 14px',
                  marginBottom: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  flexWrap: 'wrap',
                }}
              >
                <input
                  data-broiler-dailys-saved-view-name
                  type="text"
                  value={saveViewName}
                  placeholder="View name"
                  onChange={(e) => setSaveViewName(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') submitSaveView();
                  }}
                  style={{...fi, flex: 1, minWidth: 200}}
                />
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveBroilerDailysViewVisibility"
                    checked={saveViewVisibility === 'private'}
                    onChange={() => setSaveViewVisibility('private')}
                    data-broiler-dailys-saved-view-visibility="private"
                  />
                  Private
                </label>
                <label style={savedViewRadioLabelS}>
                  <input
                    type="radio"
                    name="saveBroilerDailysViewVisibility"
                    checked={saveViewVisibility === 'public'}
                    onChange={() => setSaveViewVisibility('public')}
                    data-broiler-dailys-saved-view-visibility="public"
                  />
                  Public
                </label>
                <Button
                  type="button"
                  data-broiler-dailys-saved-view-save
                  onClick={submitSaveView}
                  disabled={savedViewBusy}
                >
                  Save
                </Button>
                <Button type="button" onClick={() => setShowSaveViewForm(false)} disabled={savedViewBusy}>
                  Cancel
                </Button>
              </div>
            )}
          </>
        )}
        {exportNotice && <div style={{marginBottom: 14, color: '#b91c1c', fontSize: 12}}>{exportNotice}</div>}
        <div style={{display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center'}}>
          <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} style={{...fi, width: 130}} />
          <span style={{fontSize: 12, color: 'var(--ink-muted)'}}>to</span>
          <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} style={{...fi, width: 130}} />
          <select value={fBatch} onChange={(e) => setFBatch(e.target.value)} style={fi}>
            <option value="">All groups</option>
            {batchOpts.map((b) => (
              <option key={b} value={b}>
                {b}
              </option>
            ))}
          </select>
          <select
            value={teamMemberSelectValue(fTeam, teamOpts)}
            onChange={(e) => setFTeam(e.target.value)}
            style={fi}
            data-broiler-dailys-team-filter="1"
          >
            <option value="">All team members</option>
            {teamOpts.map((t) => (
              <option key={t} value={t}>
                {t}
              </option>
            ))}
          </select>
          {(fBatch || fTeam || fFrom || fTo || srcFilter !== 'all') && (
            <Button
              onClick={() => {
                setFBatch('');
                setFTeam('');
                setFFrom('');
                setFTo('');
                setSrcFilter('all');
              }}
            >
              Clear
            </Button>
          )}
          <Segmented
            aria-label="Report Source"
            value={srcFilter}
            onChange={setSrcFilter}
            options={[
              {value: 'all', label: 'All'},
              {value: 'daily', label: 'Daily Reports'},
              {value: 'addfeed', label: 'Add Feed'},
            ]}
            style={{marginLeft: 'auto'}}
          />
          <Button
            type="button"
            data-broiler-dailys-export-csv="1"
            onClick={handleExportCsv}
            disabled={loading || !!loadError}
          >
            Export CSV
          </Button>
          <Button
            type="button"
            data-broiler-dailys-print="1"
            onClick={handlePrintRows}
            disabled={loading || !!loadError}
          >
            Print
          </Button>
        </div>
        <InlineNotice notice={loadError} />
        {loadError && (
          <Button
            type="button"
            data-daily-list-retry="1"
            onClick={() => setReloadKey((k) => k + 1)}
            style={{marginBottom: 12}}
          >
            Retry
          </Button>
        )}
        {loading && <div style={{textAlign: 'center', padding: '3rem', color: 'var(--ink-faint)'}}>Loading...</div>}
        <OperationalListEmptyState
          loading={loading}
          loadError={loadError}
          totalCount={records.length}
          filteredCount={filtered.length}
          emptyLabel="No broiler daily reports yet"
        />
        {!loading && !loadError && filtered.length > 0 && (
          <DailyCardList
            program="broiler"
            rows={filtered}
            fmt={fmt}
            maxInitialRows={100}
            onOpen={(d) =>
              navigate('/broiler/dailys/' + d.id, recordSeqNavOptions(dailySeqItems(filtered, 'batch_label')))
            }
            rowAttrs={(d) => ({'data-daily-row': d.id})}
            mapRow={(raw) => {
              const d = raw as BroilerDailyRecord;
              return {
                name: d.batch_label || '—',
                // R06: a 7px dot in the batch's own group colour. The colour
                // comes from the name's trailing number, so the row needs no
                // batch record to draw it.
                dot: d.batch_label ? getBatchColor(d.batch_label).bg : null,
                team: d.team_member || '—',
                source: d.source,
                photos: d.photos,
                vals: {
                  feed: feedLbsVal(d.feed_lbs),
                  feedTag: d.feed_type ? tagVal(d.feed_type) : '',
                  grit: gritVal(d.grit_lbs),
                },
                checks: [check('Moved', d.group_moved !== false), check('Waterer', d.waterer_checked !== false)],
                mort: mortText(d.mortality_count, d.mortality_reason),
                comment: commentText(d.comments),
              };
            }}
          />
        )}
        {listHasMore && (
          <div style={{textAlign: 'center', padding: '0.5rem', fontSize: 11, color: 'var(--ink-faint)'}}>
            Loading more records...
          </div>
        )}
      </div>
    </div>
  );
};

export default BroilerDailysRouter;
